import { useNextRouterContext } from "@faire/deprecated-retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { useDebouncedValue } from "@faire/web/ui/hooks/useDebouncedValue";
import once from "lodash/once";
import { useMemo } from "react";

/**
 * Return a function to refetch the RSC payload for the next page with a debounce.
 * The function also removes excessive preload links generated by ReactDOM image preloading.
 */
export const usePrefetchRSCPayloadForNextPage = (
  nextPageLink: string
): (() => void) => {
  const { prefetch: nextPrefetch } = useNextRouterContext();
  // A trick to NOT prefetch when user clicks next page, and the "scrolling to
  // top" causes intersection observer to trigger prefetching.
  // In this case, intersection observer still calls the prefetch function, but
  // since the next page link is debounced, the prefetch function is stale, thus
  // would be de-dupped.
  // When user then scrolls back down to the pagination bar, the next page link
  // is updated, and the prefetch function is called again.
  const debouncedNextPageLink = useDebouncedValue(nextPageLink, 1000);
  return useMemo(
    () =>
      once(() => {
        // Remove excessive preload links generated by ReactDOM image preloading
        const preloadLinks = getWindowOrThrow().document.querySelectorAll(
          'link[rel="preload"]'
        );
        preloadLinks.forEach((link) => link.remove());

        nextPrefetch(debouncedNextPageLink);
      }),
    [debouncedNextPageLink, nextPrefetch]
  );
};
